package app.footyguide.web.data

/**
 * Firestore document wrapper for the Firebase JS SDK.
 *
 * Unlike FirestoreDocument (REST format with {stringValue: "..."} wrappers),
 * the SDK returns plain JS objects where field values are directly accessible:
 *   string -> String, number -> Int/Double, array -> js array, map -> js object
 */
class SdkDocument(
    val id: String,
    private val data: dynamic,
) {
    fun string(key: String): String? = data[key] as? String
    fun boolean(key: String): Boolean? = data[key] as? Boolean
    fun int(key: String): Int? = (data[key] as? Number)?.toInt()

    fun dynamicList(key: String): List<dynamic> {
        val arr = data[key] ?: return emptyList()
        val length = (arr.length as? Number)?.toInt() ?: 0
        return (0 until length).map { arr[it] }
    }

    fun stringList(key: String): List<String> {
        val arr = data[key] ?: return emptyList()
        val length = (arr.length as? Number)?.toInt() ?: 0
        return (0 until length).mapNotNull { arr[it] as? String }
    }

    fun stringMap(key: String): Map<String, String> {
        val obj = data[key] ?: return emptyMap()
        val keys = js("Object.keys")(obj) as? Array<String> ?: return emptyMap()
        return keys.mapNotNull { k ->
            val v = obj[k] as? String
            if (v != null) k to v else null
        }.toMap()
    }

    fun stringListMap(key: String): Map<String, List<String>> {
        val obj = data[key] ?: return emptyMap()
        val keys = js("Object.keys")(obj) as? Array<String> ?: return emptyMap()
        return keys.mapNotNull { k ->
            val arr = obj[k]
            if (arr != null && arr != undefined) {
                val length = (arr.length as? Number)?.toInt() ?: 0
                val list = (0 until length).mapNotNull { arr[it] as? String }
                k to list
            } else null
        }.toMap()
    }

    /** Access a nested map/object field (e.g. "theme" -> {primaryColor: "...", isDark: true}). */
    fun nestedMap(key: String): SdkNestedMap? {
        val obj = data[key] ?: return null
        return SdkNestedMap(obj)
    }
}

/** Typed accessor for a nested Firestore map (SDK format — plain JS object). */
class SdkNestedMap(private val obj: dynamic) {
    fun nestedMap(key: String): SdkNestedMap? {
        val value = obj[key] ?: return null
        return SdkNestedMap(value)
    }

    fun string(key: String): String? = obj[key] as? String
    fun boolean(key: String): Boolean? = obj[key] as? Boolean
    fun int(key: String): Int? = (obj[key] as? Number)?.toInt()
}
